import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Card, Form, Row, Col, Button, Table, Alert, Badge } from 'react-bootstrap';
import { addProjectAction } from '../../store/reducers/project';
import { accountAuthInfoPagingAction } from '../../store/reducers/account';
import Select from 'react-select';
import DatePicker from '@app/components/CustomDatePicker';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Get, Post } from '../../utils/httpProvider';
import { ANNUAL_JOB_API, POSITION_API, ACCOUNT_POSITION_API, JOBGROUP_API } from '../../utils/apiUrl';
import { checkIsMemberC9 } from '../../utils/define';

const AssignedJobAdd = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { listAccount } = useSelector((state) => state.account);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [jobGroups, setJobGroups] = useState([]);
    const [selectedJobGroupId, setSelectedJobGroupId] = useState('');
    const [catalogJobs, setCatalogJobs] = useState([]);
    const [selectedAnnualJobId, setSelectedAnnualJobId] = useState('');
    const [positions, setPositions] = useState([]);
    const [selectedPositionId, setSelectedPositionId] = useState('');
    const [accountPositions, setAccountPositions] = useState({});
    const [memberPercents, setMemberPercents] = useState({});

    useEffect(() => {
        dispatch(accountAuthInfoPagingAction({ pageIndex: 1, pageSize: 1000 }));
        
        Get(JOBGROUP_API.GET_ALL, null, null)
            .then((res) => { if (res) setJobGroups(res); })
            .catch((err) => console.error('Lỗi tải nhóm công việc', err));

        Get(ANNUAL_JOB_API.GET_ALL_ACTIVE, null, null)
            .then((res) => { if (res) setCatalogJobs(res); })
            .catch((err) => console.error('Lỗi tải danh mục công việc', err));

        Get(POSITION_API.GET_ALL_ACTIVE, null, null)
            .then((res) => { if (res) setPositions(res); })
            .catch((err) => console.error('Lỗi tải danh sách vị trí', err));
    }, [dispatch]);

    useEffect(() => {
        const fetchAccountPositions = async () => {
            if (listAccount && listAccount.length > 0) {
                const ids = listAccount.map(item => {
                    const acc = item.account || item.Account || item.accountResponse || item.AccountResponse;
                    return item.accountId || item.AccountId || item.id || item.Id || acc?.id || acc?.Id;
                }).filter(Boolean);

                try {
                    const response = await Post(ACCOUNT_POSITION_API.GET_BY_ACCOUNT_IDS, null, ids);
                    const res = await response.json();
                    const posList = Array.isArray(res) ? res : (res?.data || []);
                    const map = {};
                    posList.forEach(pos => {
                        map[pos.accountId || pos.AccountId] = pos.positionId || pos.PositionId;
                    });
                    setAccountPositions(map);
                } catch (error) {
                    console.error('Lỗi tải vị trí của tài khoản', error);
                }
            } else {
                setAccountPositions({});
            }
        };
        fetchAccountPositions();
    }, [listAccount]);

    const availableJobs = selectedJobGroupId
        ? catalogJobs.filter(j => String(j.jobGroupId || j.JobGroupId) === String(selectedJobGroupId))
        : [];

    const selectedJob = catalogJobs.find(jg => String(jg.id) === String(selectedAnnualJobId));
    const jobGroupWeight = selectedJob ? Number(selectedJob.weight ?? selectedJob.Weight ?? selectedJob.jobGroupWeight ?? 0) : 0;

    const memberOptions = listAccount
        .filter(item => {
            const acc = item.account || item.Account || item.accountResponse || item.AccountResponse;
            const roles = item.roles || item.Roles || acc?.roles || acc?.Roles || [];
            if (!checkIsMemberC9(roles)) return false;

            if (selectedPositionId) {
                const id = item.accountId || item.AccountId || item.id || item.Id || acc?.id || acc?.Id;
                const posId = accountPositions[id];
                return String(posId) === String(selectedPositionId);
            }
            return true;
        })
        .map(item => {
            const acc = item.account || item.Account || item.accountResponse || item.AccountResponse;
            const id = item.accountId || item.AccountId || item.id || item.Id || acc?.id || acc?.Id;
            const username = item.username || item.Username || acc?.username || acc?.Username;
            const authInfo = item.accountAuthInfo || item.AccountAuthInfo || item;
            const lastName = authInfo.lastName || '';
            const middleName = authInfo.middleName || '';
            const firstName = authInfo.firstName || '';
            const fullName = `${lastName} ${middleName} ${firstName}`.trim().replace(/\s+/g, ' ');
            return { value: id, label: fullName ? `${fullName} (${username})` : username || id };
        });

    const selectedMemberIds = Object.keys(memberPercents);

    const handleMembersChange = useCallback((selected) => {
        const newIds = selected ? selected.map(x => x.value) : [];
        setMemberPercents(prev => {
            const next = {};
            if (newIds.length === 0) return next;
            const perPerson = newIds.length > 0 ? (100 / newIds.length) : 0;
            newIds.forEach(uid => {
                next[uid] = prev[uid] !== undefined ? prev[uid] : perPerson;
            });
            return next;
        });
    }, [jobGroupWeight]);

    const handlePositionChange = (selectedOption) => {
        const newPosId = selectedOption ? selectedOption.value : '';
        setSelectedPositionId(newPosId);
        setMemberPercents({});
    };

    const isAllSelected = memberOptions.length > 0 && selectedMemberIds.length === memberOptions.length && memberOptions.every(opt => selectedMemberIds.includes(opt.value));

    const handleSelectAllChange = (e) => {
        const checked = e.target.checked;
        if (checked) {
            handleMembersChange(memberOptions);
        } else {
            handleMembersChange([]);
        }
    };

    const handleJobGroupChange = (selectedOption) => {
        const newGroupId = selectedOption ? selectedOption.value : '';
        setSelectedJobGroupId(newGroupId);
        setSelectedAnnualJobId('');
    };

    const handleJobChange = (selectedOption) => {
        const newJobId = selectedOption ? selectedOption.value : '';
        setSelectedAnnualJobId(newJobId);
    };

    const totalPercent = Object.values(memberPercents).reduce((s, p) => s + Number(p || 0), 0);
    const totalWeight = (jobGroupWeight * totalPercent) / 100;

    const handlePercentChange = (uid, val) => {
        setMemberPercents(prev => ({ ...prev, [uid]: val === '' ? '' : Number(val) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) { toast.error('Vui lòng nhập tên công việc'); return; }
        const hasInvalidPercent = Object.values(memberPercents).some(p => Number(p || 0) < 0);
        if (hasInvalidPercent) {
            toast.error('Tỉ lệ của từng thành viên không được âm');
            return;
        }
        const members = Object.entries(memberPercents).map(([uid, p]) => {
            const acc = (listAccount || []).find(item => {
                const accNested = item.account || item.Account || item.accountResponse || item.AccountResponse;
                const id = item.accountId || item.AccountId || item.id || item.Id || accNested?.id || accNested?.Id;
                return String(id).toLowerCase() === String(uid).toLowerCase();
            });
            const authInfo = acc?.accountAuthInfo || acc?.AccountAuthInfo || acc;
            const email = authInfo?.email || authInfo?.Email || acc?.email || acc?.Email || acc?.account?.email || '';
            const lastName = authInfo?.lastName || authInfo?.LastName || '';
            const middleName = authInfo?.middleName || authInfo?.MiddleName || '';
            const firstName = authInfo?.firstName || authInfo?.FirstName || '';
            const fullName = `${lastName} ${middleName} ${firstName}`.trim().replace(/\s+/g, ' ') || acc?.username || '';
            return {
                userId: uid,
                weight: (jobGroupWeight * Number(p || 0)) / 100,
                email,
                fullName
            };
        });

        const model = {
            name: name.trim(),
            description: description.trim(),
            status: 0,
            startDate: startDate || null,
            endDate: endDate || null,
            memberIds: selectedMemberIds,
            members,
            annualJobId: selectedAnnualJobId ? Number(selectedAnnualJobId) : null,
            jobGroupId: selectedJobGroupId ? Number(selectedJobGroupId) : null,
            positionId: selectedPositionId || null,
            projectType: 1
        };

        try {
            setSubmitting(true);
            await dispatch(addProjectAction(model)).unwrap();
            toast.success('Giao công việc thành công');
            history.push('/assigned-job');
        } catch (err) {
            toast.error('Giao công việc thất bại');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card>
            <Card.Header as="h5">Giao công việc mới</Card.Header>
            <Card.Body>
                <Form onSubmit={handleSubmit}>
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Tên công việc <span className="text-danger">*</span></Form.Label>
                        <Col sm={10}>
                            <Form.Control
                                type="text"
                                placeholder="Nhập tên công việc"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Vị trí</Form.Label>
                        <Col sm={10}>
                            <Select
                                isClearable
                                placeholder="-- Tất cả vị trí --"
                                value={selectedPositionId ? {
                                    value: selectedPositionId,
                                    label: (() => {
                                        const pos = positions.find(p => String(p.id) === String(selectedPositionId));
                                        return pos ? `${pos.name} (${pos.code})` : '';
                                    })()
                                } : null}
                                onChange={handlePositionChange}
                                options={positions.map((pos) => ({
                                    value: pos.id,
                                    label: `${pos.name} (${pos.code})`
                                }))}
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Nhóm công việc</Form.Label>
                        <Col sm={10}>
                            <Select
                                isClearable
                                placeholder="-- Chọn nhóm công việc --"
                                value={selectedJobGroupId ? {
                                    value: selectedJobGroupId,
                                    label: (() => {
                                        const group = jobGroups.find(jg => String(jg.id) === String(selectedJobGroupId));
                                        return group ? group.name : '';
                                    })()
                                } : null}
                                onChange={handleJobGroupChange}
                                options={jobGroups.map((jg) => ({
                                    value: jg.id,
                                    label: jg.name
                                }))}
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Danh mục công việc</Form.Label>
                        <Col sm={10}>
                            <Select
                                isClearable
                                placeholder={selectedJobGroupId ? "-- Chọn danh mục công việc --" : "-- Hãy chọn nhóm công việc trước --"}
                                isDisabled={!selectedJobGroupId}
                                value={selectedAnnualJobId ? {
                                    value: selectedAnnualJobId,
                                    label: (() => {
                                        const job = catalogJobs.find(j => String(j.id) === String(selectedAnnualJobId));
                                        return job ? `${job.name} - Điểm: ${job.weight ?? job.Weight ?? job.jobGroupWeight ?? 0}` : '';
                                    })()
                                } : null}
                                onChange={handleJobChange}
                                options={availableJobs.map((job) => ({
                                    value: job.id,
                                    label: `${job.name} - Điểm: ${job.weight ?? job.Weight ?? job.jobGroupWeight ?? 0}`
                                }))}
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Ngày bắt đầu</Form.Label>
                        <Col sm={4}>
                            <DatePicker
                                className="form-control"
                                selected={startDate ? new Date(startDate) : null}
                                onChange={(date) => setStartDate(date ? moment(date).format('YYYY-MM-DD') : '')}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="dd/MM/yyyy"
                                isClearable
                            />
                        </Col>
                        <Form.Label column sm={2}>Ngày kết thúc dự kiến</Form.Label>
                        <Col sm={4}>
                            <DatePicker
                                className="form-control"
                                selected={endDate ? new Date(endDate) : null}
                                onChange={(date) => setEndDate(date ? moment(date).format('YYYY-MM-DD') : '')}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="dd/MM/yyyy"
                                isClearable
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Mô tả</Form.Label>
                        <Col sm={10}>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={2}>Người tham gia</Form.Label>
                        <Col sm={10}>
                            <div className="d-flex align-items-center mb-2">
                                <Form.Check
                                    type="checkbox"
                                    id="select-all-members-chk"
                                    label="Chọn tất cả thành viên thuộc vị trí này"
                                    checked={isAllSelected}
                                    onChange={handleSelectAllChange}
                                    disabled={memberOptions.length === 0}
                                    className="user-select-none"
                                />
                            </div>
                            <Select
                                isMulti
                                options={memberOptions}
                                placeholder="Gõ tên để tìm kiếm và chọn thành viên..."
                                value={memberOptions.filter(opt => selectedMemberIds.includes(opt.value))}
                                onChange={handleMembersChange}
                                classNamePrefix="select"
                                isSearchable={true}
                                isClearable={true}
                                noOptionsMessage={() => 'Không tìm thấy thành viên nào có quyền APP_KPI_C9'}
                            />
                        </Col>
                    </Form.Group>

                    {selectedMemberIds.length > 0 && (
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm={2}>
                                Trọng số thành viên
                                {selectedJob && (
                                    <div className="mt-1">
                                        <Badge variant="info" style={{ fontSize: '0.75rem' }}>
                                            Nhóm: {jobGroupWeight}
                                        </Badge>
                                    </div>
                                )}
                            </Form.Label>
                            <Col sm={10}>
                                {!selectedJob && (
                                    <Alert variant="warning" className="py-2 mb-2" style={{ fontSize: 13 }}>
                                        Chọn danh mục công việc để tự động tính trọng số
                                    </Alert>
                                )}
                                <Table bordered size="sm" style={{ fontSize: 13 }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th>Thành viên</th>
                                            <th className="text-center" style={{ width: 100 }}>Tỉ lệ (%)</th>
                                            <th style={{ width: 160 }}>
                                                Điểm
                                                {selectedJob && (
                                                    <span className="text-muted ms-1" style={{ fontSize: 11 }}>
                                                        (Tổng: {totalWeight.toFixed(2)}/{jobGroupWeight})
                                                    </span>
                                                )}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedMemberIds.map(uid => {
                                            const opt = memberOptions.find(o => String(o.value) === String(uid));
                                            return (
                                                <tr key={uid}>
                                                    <td className="align-middle">{opt?.label || uid}</td>
                                                    <td className="align-middle text-center">
                                                        <Form.Control
                                                            type="number"
                                                            min={0}
                                                            step={0.01}
                                                            value={memberPercents[uid] ?? 0}
                                                            onChange={(e) => handlePercentChange(uid, e.target.value)}
                                                            size="sm"
                                                            style={{ width: 120, margin: '0 auto' }}
                                                        />
                                                    </td>
                                                    <td className="align-middle fw-bold">
                                                        {((jobGroupWeight * (memberPercents[uid] ?? 0)) / 100).toFixed(2)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </Col>
                        </Form.Group>
                    )}

                    <Form.Group as={Row}>
                        <Col sm={{ span: 10, offset: 2 }}>
                            <div className="d-flex gap-2">
                                <Button type="submit" variant="primary" disabled={submitting}>
                                    {submitting ? 'Đang lưu...' : 'Giao công việc'}
                                </Button>
                                <Button variant="secondary" onClick={() => history.push('/assigned-job')} disabled={submitting}>
                                    Hủy
                                </Button>
                            </div>
                        </Col>
                    </Form.Group>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default AssignedJobAdd;
